import { useState } from 'react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';

import { describeAction, describeProgress, formatTime } from './cutover-view';
import { AUDIT_LOG_PAGE_SIZE, useCutoverActionsQuery } from './useCutover';

import type { CutoverAction } from './cutover.types';

const STATUS_VARIANT: Record<
  CutoverAction['status'],
  'default' | 'secondary' | 'destructive' | 'outline'
> = {
  refused: 'outline',
  queued: 'secondary',
  running: 'secondary',
  finished: 'default',
  failed: 'destructive',
};

export function CutoverAuditLog({
  isMoveRunning,
  version,
}: {
  isMoveRunning: boolean;
  version: string;
}) {
  const [page, setPage] = useState(1);
  const { data, isLoading, error } = useCutoverActionsQuery(
    page,
    isMoveRunning,
    version
  );
  const totalPages = Math.ceil((data?.total ?? 0) / AUDIT_LOG_PAGE_SIZE);

  return (
    <div className="space-y-4">
      {error && <p className="text-sm text-destructive">{error.message}</p>}
      <Table containerClassName="rounded-md border">
        <TableHeader>
          <TableRow>
            <TableHead>When</TableHead>
            <TableHead>Who</TableHead>
            <TableHead>What</TableHead>
            <TableHead>Step</TableHead>
            <TableHead>Reason</TableHead>
            <TableHead>Result</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {isLoading && (
            <TableRow>
              <TableCell colSpan={6} className="text-muted-foreground">
                Loading the audit log...
              </TableCell>
            </TableRow>
          )}
          {data?.actions.map((action) => (
            <TableRow key={action.id}>
              <TableCell className="whitespace-nowrap align-top">
                <time dateTime={action.created_at}>
                  {formatTime(action.created_at)}
                </time>
              </TableCell>
              <TableCell className="align-top">
                {action.actor}
                {action.actor_kind === 'operator' && (
                  <p className="text-xs text-muted-foreground">
                    Break-glass Job
                  </p>
                )}
              </TableCell>
              <TableCell className="align-top">
                {describeAction(action)}
              </TableCell>
              <TableCell className="align-top">{action.step_label}</TableCell>
              <TableCell className="max-w-xs whitespace-pre-wrap align-top">
                {action.reason ?? '-'}
                {action.overridden && (
                  <Badge variant="outline" className="ml-2">
                    Override
                  </Badge>
                )}
              </TableCell>
              <TableCell className="max-w-md space-y-1 align-top">
                <Badge variant={STATUS_VARIANT[action.status]}>
                  {action.status}
                </Badge>
                {action.status !== 'refused' && (
                  <p className="text-xs text-muted-foreground">
                    {describeProgress(action)}
                  </p>
                )}
                {action.outcome && (
                  <p className="whitespace-pre-wrap text-xs">
                    {action.outcome}
                  </p>
                )}
              </TableCell>
            </TableRow>
          ))}
          {data?.actions.length === 0 && (
            <TableRow>
              <TableCell colSpan={6} className="text-muted-foreground">
                Nobody has asked for a cutover action yet.
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
      {totalPages > 1 && (
        <div className="flex items-center gap-2 text-sm">
          <Button
            variant="outline"
            size="sm"
            disabled={page <= 1}
            onClick={() => setPage(page - 1)}
          >
            Previous
          </Button>
          <span>
            Page {page} of {totalPages}
          </span>
          <Button
            variant="outline"
            size="sm"
            disabled={page >= totalPages}
            onClick={() => setPage(page + 1)}
          >
            Next
          </Button>
        </div>
      )}
    </div>
  );
}
